/**
 * Sanity MCP Client
 * 
 * Client for calling Sanity operations via API routes or direct MCP functions.
 * This provides a clean interface for interacting with Sanity CMS.
 * 
 * @version 1.0.0
 * @author Claude Code
 */

import type { ApiRequest } from '@/features/gallery/api/sanity';

export interface SanityResource {
  projectId: string;
  dataset: string;
  target: 'dataset';
}

export interface CreateDocumentParams {
  resource: SanityResource;
  type: string;
  workspaceName: string;
  instruction: string[];
}

export interface UpdateDocumentParams {
  resource: SanityResource;
  workspaceName: string;
  operations: {
    documentId: string;
    instruction: string;
  }[];
}

export interface QueryDocumentsParams {
  resource: SanityResource;
  query: string;
  params?: Record<string, any>;
  single?: boolean;
  limit?: number;
}

export interface PatchDocumentParams {
  resource: SanityResource;
  workspaceName: string;
  documentId: string;
  operation: {
    op: 'set' | 'unset' | 'append';
    path: string;
    value?: any;
  };
}

export interface DeleteDocumentParams {
  resource: SanityResource;
  id: string;
}

/**
 * Sanity MCP Client class
 * Provides methods to interact with Sanity CMS via MCP
 */
export class SanityMcpClient {
  private static readonly PROJECT_ID = '2jg18uur';
  private static readonly DATASET = 'production';

  private static getResource(): SanityResource {
    return {
      projectId: this.PROJECT_ID,
      dataset: this.DATASET,
      target: 'dataset'
    };
  }

  /**
   * Call Sanity API with fallback to local handler
   */
  private static async callSanityApi(request: ApiRequest): Promise<any> {
    const response = await fetch('/api/sanity-gallery', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Sanity API failed (${response.status}): ${text}`);
    }

    const result = await response.json();
    return result.data || result;
  }

  /**
   * Create a new document in Sanity
   */
  static async createDocument(type: string, instruction: string[]): Promise<{ _id: string }> {
    try {
      // Try API endpoint first, fallback to local handler
      const result = await this.callSanityApi({
        action: 'create_document',
        type,
        instruction,
        resource: this.getResource(),
        workspaceName: 'default'
      });
      
      if (result.documents?.[0]?._id) {
        return { _id: result.documents[0]._id };
      }
      
      throw new Error('No document ID returned from Sanity');
    } catch (error) {
      console.error('Error creating document in Sanity:', error);
      throw new Error(`Failed to create document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update an existing document in Sanity
   */
  static async updateDocument(documentId: string, instruction: string): Promise<void> {
    try {
      await this.callSanityApi({
        action: 'update_document',
        documentId,
        instruction,
        resource: this.getResource(),
        workspaceName: 'default'
      });
    } catch (error) {
      console.error('Error updating document in Sanity:', error);
      throw new Error(`Failed to update document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Query documents from Sanity
   */
  static async queryDocuments<T = any>(query: string, params?: Record<string, any>, options?: { single?: boolean; limit?: number }): Promise<T[]> {
    try {
      const result = await this.callSanityApi({
        action: 'query_documents',
        query,
        params,
        resource: this.getResource(),
        single: options?.single,
        limit: options?.limit
      });
      
      return Array.isArray(result) ? result : (result?.data ? (Array.isArray(result.data) ? result.data : []) : []);
    } catch (error) {
      console.error('Error querying documents from Sanity:', error);
      throw new Error(`Failed to query documents: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get a single document by ID
   */
  static async getDocument<T = any>(documentId: string): Promise<T | null> {
    try {
      const result = await this.callSanityApi({
        action: 'get_document',
        documentId,
        resource: this.getResource()
      });
      
      return result?.data || result || null;
    } catch (error) {
      console.error('Error getting document from Sanity:', error);
      return null;
    }
  }

  /**
   * Patch a document in Sanity
   */
  static async patchDocument(documentId: string, operation: { op: 'set' | 'unset' | 'append'; path: string; value?: any }): Promise<void> {
    try {
      await this.callSanityApi({
        action: 'patch_document',
        documentId,
        operation,
        resource: this.getResource(),
        workspaceName: 'default'
      });
    } catch (error) {
      console.error('Error patching document in Sanity:', error);
      throw new Error(`Failed to patch document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Delete a document from Sanity
   */
  static async deleteDocument(documentId: string): Promise<void> {
    try {
      await this.callSanityApi({
        action: 'delete_document',
        documentId,
        resource: this.getResource()
      });
    } catch (error) {
      console.error('Error deleting document from Sanity:', error);
      throw new Error(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Search galleries by title
   */
  static async searchGalleries(searchTerm: string): Promise<any[]> {
    return this.queryDocuments(
      `*[_type == "gallery" && (title match $searchTerm || slug.current match $searchTerm)] | order(_createdAt desc)`,
      { searchTerm: `*${searchTerm}*` },
      { limit: 20 }
    );
  }
}

export default SanityMcpClient;
